import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkAuth = useCallback(async () => {
    const hasSessionMarker = window.localStorage?.getItem('fieldcrm-has-session') === 'true';
    if (window.location.pathname === '/login' && !hasSessionMarker) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      window.localStorage?.removeItem('fieldcrm-has-session');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const handleExpired = () => setUser(false);
    window.addEventListener('fieldcrm-auth-expired', handleExpired);
    return () => window.removeEventListener('fieldcrm-auth-expired', handleExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    window.localStorage?.setItem('fieldcrm-has-session', 'true');
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      window.localStorage?.removeItem('fieldcrm-has-session');
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
